import { useEffect, useRef, useState } from "react";
import { api, API_BASE, getActiveBook } from "../api";
import { InlineGraph, Chart } from "./AdvisorVisuals";
import { Sub } from "./GraphCanvas";

type Visual =
  | { type: "graph"; sub: Sub; highlight_ids: string[]; caption?: string }
  | { type: "chart"; chart_type: string; title: string; labels: string[]; values: number[] };

type Turn = {
  question: string;
  answer: string;
  toolCalls: { tool: string; input: unknown }[];
  visuals: Visual[];
  status: "running" | "done" | "error";
  error?: string;
};

const TOOL_LABEL: Record<string, string> = {
  graph_trace_path: "tracing the decision graph…",
  graph_search: "searching the graph…",
  graph_node_detail: "reading a graph node…",
  performance_summary: "checking performance…",
  holdings: "checking holdings…",
  ledger: "checking the order ledger…",
  activity_search: "searching the activity log…",
  run_diagnostics: "running full diagnostics…",
  stand_down_review: "checking whether that stand-down was right…",
  reconciliation_check: "ticking and tying every venue…",
  research_symbol: "researching that symbol…",
  flag_feedback: "noting that for review…",
  fockspace_search: "searching fock.space…",
  fockspace_premise_analysis: "checking thesis coupling on fock.space…",
  fockspace_anchor_weather: "checking fock.space anchor weather…",
};

export default function AdvisorChat() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [turns, setTurns] = useState<Turn[]>([]);
  const [weather, setWeather] = useState("");
  const esRef = useRef<EventSource | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const weatherFetched = useRef(false);
  const historyFetched = useRef(false);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [turns, open]);

  useEffect(() => () => esRef.current?.close(), []);

  // Proactive fock.space anchor weather — fetch once, the first time the
  // panel opens (not on every open/close toggle).
  useEffect(() => {
    if (!open || weatherFetched.current) return;
    weatherFetched.current = true;
    api.get<{ weather: string }>("/api/advisor/weather")
      .then((r) => setWeather(r.weather))
      .catch(() => { /* quiet — not worth surfacing a failed ambient check */ });
  }, [open]);

  // Restore the last conversation for this book — it was already being
  // logged server-side the whole time, the panel just never read it back,
  // so closing/reloading looked like the chat forgot everything.
  useEffect(() => {
    if (!open || historyFetched.current) return;
    historyFetched.current = true;
    type HistoryTurn = { question: string; answer: string; tools_used: string[] };
    api.get<HistoryTurn[]>("/api/advisor/history")
      .then((rows) => {
        if (rows.length === 0) return;
        setTurns((p) => (p.length > 0 ? p : rows.map((r) => ({
          question: r.question, answer: r.answer,
          toolCalls: r.tools_used.map((tool) => ({ tool, input: {} })),
          visuals: [], status: "done" as const,
        }))));
      })
      .catch(() => { /* quiet — starts blank, same as before this existed */ });
  }, [open]);

  async function send() {
    const question = input.trim();
    if (!question) return;
    setInput("");
    const idx = turns.length;
    const history = turns
      .filter((t) => t.status === "done" && t.answer)
      .map((t) => [{ role: "user", content: t.question }, { role: "assistant", content: t.answer }])
      .flat();
    setTurns((p) => [...p, { question, answer: "", toolCalls: [], visuals: [], status: "running" }]);

    let session_id: string;
    try {
      const res = await api.post<{ session_id: string }>("/api/advisor/messages", {
        question, book: getActiveBook(), history,
      });
      session_id = res.session_id;
    } catch (e) {
      setTurns((p) => p.map((t, i) => (i === idx ? { ...t, status: "error", error: (e as Error).message } : t)));
      return;
    }

    esRef.current?.close();
    const es = new EventSource(
      `${API_BASE}/api/advisor/stream?session=${encodeURIComponent(session_id)}&book=${getActiveBook()}`,
      { withCredentials: true },
    );
    es.addEventListener("chat_progress", (m) => {
      try {
        const { payload } = JSON.parse((m as MessageEvent).data);
        setTurns((p) => p.map((t, i) => (i === idx ? { ...t, toolCalls: [...t.toolCalls, payload] } : t)));
      } catch { /* ignore */ }
    });
    es.addEventListener("chat_visual", (m) => {
      try {
        const { payload } = JSON.parse((m as MessageEvent).data);
        const visual: Visual = payload.type === "graph"
          ? { type: "graph", sub: payload.sub, highlight_ids: payload.highlight_ids, caption: payload.caption }
          : { type: "chart", chart_type: payload.chart_type, title: payload.title,
              labels: payload.labels, values: payload.values };
        setTurns((p) => p.map((t, i) => (i === idx ? { ...t, visuals: [...t.visuals, visual] } : t)));
      } catch { /* ignore */ }
    });
    es.addEventListener("chat_done", (m) => {
      try {
        const { payload } = JSON.parse((m as MessageEvent).data);
        setTurns((p) => p.map((t, i) => (i === idx ? { ...t, answer: payload.answer, status: "done" } : t)));
      } catch { /* ignore */ }
      es.close();
    });
    es.addEventListener("chat_error", (m) => {
      let error = "the advisor hit an error";
      try { error = JSON.parse((m as MessageEvent).data).payload?.error || error; } catch { /* ignore */ }
      setTurns((p) => p.map((t, i) => (i === idx ? { ...t, status: "error", error } : t)));
      es.close();
    });
    es.onerror = () => {
      setTurns((p) => p.map((t, i) =>
        i === idx && t.status === "running" ? { ...t, status: "error", error: "connection lost" } : t));
    };
    esRef.current = es;
  }

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-40 w-14 h-14 rounded-full
                   overflow-hidden shadow-lg border-2 border-gold/70
                   hover:brightness-110 active:scale-95 transition"
        title="Ask Stonky"
      >
        <img src="/assets/stonky-icon.jpg" alt="Ask Stonky" className="w-full h-full object-cover" />
      </button>

      {open && (
        <div className="fixed inset-x-0 bottom-0 md:bottom-6 md:right-6 md:inset-x-auto z-50
                        md:w-[420px] w-full h-[70vh] md:h-[600px] card flex flex-col shadow-2xl">
          <div className="flex items-center justify-between border-b border-edge pb-2 mb-2">
            <div className="flex items-center gap-2">
              <img src="/assets/stonky-icon.jpg" alt="" className="w-6 h-6 rounded-full object-cover border border-gold/50" />
              <span className="font-semibold text-sm">Ask Stonky</span>
            </div>
            <button className="text-slate-400 hover:text-slate-200" onClick={() => setOpen(false)}>✕</button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-4 pr-1">
            {turns.length === 0 && (
              <p className="text-xs text-slate-500">
                Ask about any trade, directive, holding, or system status — Stonky's advisor is
                read-only: it researches and explains, it never places an order or changes config.
              </p>
            )}
            {weather && (
              <div className="text-xs text-gold bg-gold/10 border border-gold/30 rounded-lg px-2.5 py-2 whitespace-pre-wrap">
                <span className="font-semibold">Since we last talked — </span>
                {weather.replace(/^## ANCHOR ALERTS.*\n/, "")}
              </div>
            )}
            {turns.map((t, i) => (
              <div key={i} className="space-y-1.5">
                <div className="text-sm font-medium text-slate-200">{t.question}</div>
                {t.toolCalls.length > 0 && (
                  <div className="space-y-0.5">
                    {t.toolCalls.map((c, j) => (
                      <div key={j} className="text-xs text-slate-500 italic">
                        {TOOL_LABEL[c.tool] ?? `checking ${c.tool}…`}
                      </div>
                    ))}
                  </div>
                )}
                {t.status === "running" && !t.answer && (
                  <div className="text-xs text-slate-500 italic">thinking…</div>
                )}
                {t.status === "error" && (
                  <div className="text-xs text-down">{t.error}</div>
                )}
                {t.visuals.map((v, j) => v.type === "graph" ? (
                  <InlineGraph key={j} sub={v.sub} highlightIds={v.highlight_ids} caption={v.caption} />
                ) : (
                  <Chart key={j} chartType={v.chart_type} title={v.title} labels={v.labels} values={v.values} />
                ))}
                {t.answer && (
                  <div className="text-sm text-slate-100 whitespace-pre-wrap">{t.answer}</div>
                )}
              </div>
            ))}
          </div>

          <div className="flex gap-2 pt-2 border-t border-edge mt-2">
            <input
              className="input flex-1"
              placeholder="Ask a question…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") void send(); }}
            />
            <button className="btn btn-primary" onClick={() => void send()}>Send</button>
          </div>
        </div>
      )}
    </>
  );
}
